import styled, { createGlobalStyle } from 'styled-components';
import { motion } from 'framer-motion';
import { Card, Modal, Table } from 'antd';

export const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0.2,
      staggerChildren: 0.1,
    },
  },
};

export const StyledCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 3rem;
  transition: all 0.3s ease;
  border: 1px solid #bfdbfe;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

  &:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

export const StyledButton = styled.button`
  border-radius: 8px;
  height: 40px;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

export const ResponsiveCard = styled(StyledCard)`
  @media (max-width: 767px) {
    padding: 1rem;
    margin: 0.5rem 0;
    border-radius: 12px;
  }
`;

export const TableCard = styled(ResponsiveCard)`
  background: transparent;
  border: none;
  box-shadow: none;
  padding: 0;
  margin: 0;
  border-radius: 0;
  overflow: visible;
  
  .ant-table {
    background: transparent;
    
    .ant-table-thead > tr > th {
      background: rgba(255, 255, 255, 0.8);
      backdrop-filter: blur(8px);
      padding: 16px;
      font-weight: 600;
      font-size: 0.9rem;
      color: #1e293b;
      border-bottom: 2px solid #e2e8f0;
      transition: all 0.3s ease;
      
      &:hover {
        background: rgba(255, 255, 255, 0.95) !important;
      }
    }
    
    .ant-table-tbody > tr {
      transition: all 0.3s ease;
      
      &:hover {
        transform: translateY(-1px);
        
        > td {
          background: rgba(255, 255, 255, 0.8) !important;
          backdrop-filter: blur(8px);
        }
      }
      
      > td {
        padding: 16px;
        border-bottom: 1px solid #f1f5f9;
        
        &:first-child {
          padding-left: 24px;
        }
        
        &:last-child {
          padding-right: 24px;
        }
      }
      
      &:nth-child(even) {
        background: rgba(248, 250, 252, 0.5);
      }
    }
  }

  .ant-pagination {
    margin: 16px 24px;
    
    .ant-pagination-item {
      border-radius: 8px;
      border: 1px solid #e2e8f0;
      transition: all 0.3s ease;
      
      &:hover, &-active {
        border-color: #3b82f6;
        background: #eff6ff;
        transform: translateY(-1px);
      }
    }
  }

  @media (max-width: 767px) {
    border-radius: 16px;
    
    .ant-table {
      font-size: 0.875rem;
      
      .ant-table-thead > tr > th {
        padding: 12px 8px;
      }
      
      .ant-table-tbody > tr > td {
        padding: 12px 8px;
      }
    }
  }
`;

export const FilterContainer = styled.div`
  background: transparent;
  padding: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: visible;
  transition: all 0.3s ease;
  
  .ant-input-affix-wrapper,
  .ant-input {
    &:hover {
      border-color: #3b82f6 !important;
    }
    
    &:focus,
    &.ant-input-focused {
      border-color: #3b82f6 !important;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
    }
  }
  
  .ant-select {
    &:hover .ant-select-selector {
      border-color: #3b82f6 !important;
      background: #fafbfc !important;
    }
    
    &.ant-select-focused .ant-select-selector {
      border-color: #3b82f6 !important;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
      background: white !important;
    }
    
    &.ant-select-multiple .ant-select-selector {
      padding-top: 4px !important;
      padding-bottom: 4px !important;
    }
  }
  
  .ant-select-selector {
    border-radius: 12px !important;
    border: 2px solid #e2e8f0 !important;
    min-height: 44px !important;
    height: auto !important;
    padding: 6px 12px !important;
    transition: all 0.2s ease !important;
    background: white !important;
    font-weight: 500 !important;
    
    .ant-select-selection-search {
      margin-inline-start: 0 !important;
    }
  }
  
  .ant-select-selection-overflow {
    gap: 6px;
    align-items: center;
    
    .ant-select-selection-overflow-item {
      align-self: center;
    }
  }
  
  .ant-select-selection-item {
    background: #f1f5f9 !important;
    border: 1px solid #e2e8f0 !important;
    border-radius: 8px !important;
    color: #334155 !important;
    font-weight: 600 !important;
    padding: 3px 10px !important;
    font-size: 0.8125rem !important;
    max-width: none !important;
    height: 28px !important;
    display: flex !important;
    align-items: center !important;
    line-height: normal !important;
    
    .ant-select-selection-item-content {
      line-height: normal !important;
    }
    
    .ant-select-selection-item-remove {
      color: #64748b !important;
      margin-left: 6px !important;
      font-size: 12px !important;
      display: flex !important;
      align-items: center !important;
      
      &:hover {
        color: #1e293b !important;
        transform: scale(1.1);
      }
    }
  }
  
  .ant-picker {
    border-radius: 12px !important;
    border: 2px solid #e2e8f0 !important;
    transition: all 0.2s ease !important;
    background: white !important;
    
    &:hover {
      border-color: #3b82f6 !important;
      background: #fafbfc !important;
    }
    
    &.ant-picker-focused {
      border-color: #3b82f6 !important;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
      background: white !important;
    }
    
    .ant-picker-suffix {
      color: #94a3b8;
    }
  }
`;

export const FilterTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
  margin: 0 0 0.75rem 0;
  display: inline-block;
`;

export const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;

  .search-row {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    width: 100%;
    
    > * {
      flex: 1 1 240px;
      min-width: 240px;
      
      @media (max-width: 1024px) {
        flex: 1 1 calc(50% - 0.5rem);
        min-width: calc(50% - 0.5rem);
      }
      
      @media (max-width: 767px) {
        flex: 1 1 100%;
        min-width: 100%;
      }
    }
  }
`;

export const PageContainer = styled(motion.div)`
  padding: 24px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: calc(100vh);
  height: calc(100vh);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const GlassCard = styled(Card)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.15);
  border-radius: 16px;
  transition: all 0.3s ease;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 48px 0 rgba(31, 38, 135, 0.2);
  }
  
  .ant-card-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 0;
  }
`;

export const SearchSection = styled(GlassCard)`
  margin-bottom: 16px;
  padding: 0x;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 252, 0.95) 100%);
`;

export const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 24px;
  
  @media (max-width: 767px) {
    padding: 16px;
  }
`;

export const TableWrapper = styled.div`
  flex: 1;
  overflow: auto;
  border-radius: 16px;
  background: linear-gradient(to bottom, #f8fafc, #ffffff);
  padding: 24px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 24px;
    right: 24px;
    height: 1px;
    background: linear-gradient(to right, transparent, #e2e8f0 20%, #e2e8f0 80%, transparent);
  }
  
  @media (max-width: 767px) {
    padding: 16px;
    border-radius: 12px;
  }
`;

export const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 16px;
    overflow: hidden;
  }
  
  .ant-modal-header {
    background: linear-gradient(to right, #f8fafc, #f1f5f9);
    border-bottom: 1px solid #e2e8f0;
    padding: 20px 24px;
  }
  
  .ant-modal-body {
    padding: 24px;
  }
  
  .ant-modal-footer {
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
    padding: 16px 24px;
  }
`;

// Upload specific styles
export const UploadCard = styled(ResponsiveCard)`
  background: white;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  
  @media (max-width: 767px) {
    padding: 1rem;
  };
`;

export const UploadSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;
  
  @media (max-width: 767px) {
    flex-direction: column;
    gap: 1.5rem;
    text-align: center;
  }
`;

export const UploadContent = styled.div`
  flex: 1;
`;

export const UploadTitle = styled.h2`
  font-size: ${props => props.isMobile ? '1.5rem' : '1.875rem'};
  font-weight: 700;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.75rem;
`;

export const UploadDescription = styled.p`
  color: #64748b;
  font-size: ${props => props.isMobile ? '0.875rem' : '1rem'};
  line-height: 1.6;
  margin: 0;
`;

export const StyledUploadButton = styled(StyledButton)`
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%);
  border: none;
  padding: ${props => props.isMobile ? '0.75rem 1.5rem' : '1rem 2.5rem'};
  height: auto;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: ${props => props.isMobile ? '0.875rem' : '1rem'};
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0.05) 100%
    );
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-2px) scale(1.02);
    background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%);
    box-shadow: 
      0 12px 24px -6px rgba(37, 99, 235, 0.25),
      0 8px 16px -6px rgba(37, 99, 235, 0.15),
      0 0 0 2px rgba(59, 130, 246, 0.1);
    
    &:before {
      opacity: 1;
    }
  }
  
  &:active {
    transform: translateY(0) scale(0.98);
    box-shadow: 
      0 5px 10px -3px rgba(37, 99, 235, 0.2),
      0 2px 4px -2px rgba(37, 99, 235, 0.1);
  }

  .anticon {
    font-size: ${props => props.isMobile ? '1rem' : '1.25rem'};
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
    transition: transform 0.3s ease;
  }

  &:hover .anticon {
    transform: scale(1.1);
  }

  @media (max-width: 767px) {
    width: 100%;
    justify-content: center;
    padding: 0.875rem 1.5rem;
    border-radius: 14px;
  }
`;

export const UploadStats = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 1.5rem;

  @media (max-width: 767px) {
    gap: 1rem;
    justify-content: center;
  }
`;

export const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const StatIcon = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.bg};
  color: ${props => props.color};
`;

export const StatText = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StatLabel = styled.span`
  font-size: 0.75rem;
  color: #64748b;
`;

export const StatValue = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
`;

export const StyledTable = styled(Table)`
  .ant-table-wrapper {
    height: 100%;
    background: transparent;
  }
  
  .ant-table {
    background: transparent;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
  
  .ant-table-container {
    border: none;
  }
  
  .ant-table-thead > tr > th {
    background: #ffffff !important;
    border-bottom: 2px solid #e2e8f0;
    padding: 16px 24px;
    font-weight: 500;
    font-size: 0.75rem;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #64748b;
    position: sticky;
    top: 0;
    z-index: 10;
    transition: all 0.3s ease;
    
    &:hover {
      color: #3b82f6;
      background: #f8fafc !important;
    }
    
    &::before {
      content: none !important;
    }
    
    .ant-table-column-sorters {
      justify-content: flex-start;
      gap: 8px;
    }
    
    .ant-table-column-sorter {
      color: #cbd5e1;
      
      &.active {
        color: #3b82f6;
      }
    }
  }
  
  .ant-table-tbody > tr {
    position: relative;
    background: white;
    transition: all 0.2s ease;
    
    &:first-child td:first-child {
      border-top-left-radius: 12px;
    }
    
    &:first-child td:last-child {
      border-top-right-radius: 12px;
    }
    
    &:last-child td:first-child {
      border-bottom-left-radius: 12px;
    }
    
    &:last-child td:last-child {
      border-bottom-right-radius: 12px;
    }
    
    &:not(:last-child) td {
      border-bottom: 1px solid #f8fafc;
    }
    
    &:hover {
      background: linear-gradient(to right, #f8fafc 0%, #ffffff 50%, #f8fafc 100%);
      transform: scale(1.01);
      box-shadow: 
        0 4px 20px -2px rgba(0, 0, 0, 0.05),
        0 8px 25px -5px rgba(0, 0, 0, 0.03);
      z-index: 1;
      
      > td {
        border-bottom-color: transparent;
      }
      
      & + tr td {
        border-top: 1px solid transparent;
      }
    }
    
    > td {
      padding: 20px 24px;
      border: none;
      background: transparent;
      font-size: 0.875rem;
      color: #1e293b;
      vertical-align: middle;
      transition: all 0.2s ease;
      
      &:first-child {
        font-weight: 500;
      }
    }
  }
  
  /* Document name link styling */
  .document-link {
    color: #3b82f6;
    font-weight: 500;
    text-decoration: none;
    position: relative;
    transition: all 0.2s ease;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 0;
      height: 2px;
      background: #3b82f6;
      transition: width 0.3s ease;
    }
    
    &:hover {
      color: #2563eb;
      
      &::after {
        width: 100%;
      }
    }
  }
  
  /* Type tag improvements */
  .ant-table-tbody span[style*="borderRadius"] {
    padding: 6px 16px !important;
    font-size: 0.75rem !important;
    font-weight: 500 !important;
    letter-spacing: 0.025em;
    box-shadow: none !important;
    border: none !important;
    transition: all 0.2s ease;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
    }
  }
  
  /* Action buttons styling */
  .ant-btn {
    border-radius: 8px;
    font-size: 0.875rem;
    transition: all 0.2s ease;
    border-width: 1.5px;
    
    &:not(:disabled):hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
    
    &:not(:disabled):active {
      transform: translateY(0);
    }
    
    &[disabled] {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }
  
  /* Empty state */
  .ant-empty {
    margin: 60px 0;
    
    .ant-empty-description {
      color: #94a3b8;
      font-size: 0.875rem;
    }
  }
  
  /* Pagination styling */
  .ant-table-pagination {
    margin-top: 24px;
    padding: 0;
    
    .ant-pagination {
      .ant-pagination-item {
        border: 1.5px solid #e2e8f0;
        border-radius: 8px;
        font-weight: 500;
        transition: all 0.2s ease;
        
        &:hover {
          border-color: #3b82f6;
          color: #3b82f6;
        }
        
        &-active {
          background: #3b82f6;
          border-color: #3b82f6;
          
          a {
            color: white;
          }
        }
      }
      
      .ant-pagination-prev,
      .ant-pagination-next {
        .ant-pagination-item-link {
          border: 1.5px solid #e2e8f0;
          border-radius: 8px;
          transition: all 0.2s ease;
          
          &:hover {
            border-color: #3b82f6;
            color: #3b82f6;
          }
        }
      }
    }
  }
  
  /* Loading state */
  .ant-spin-container {
    .ant-spin {
      .ant-spin-dot {
        font-size: 32px;
        
        .ant-spin-dot-item {
          background: #3b82f6;
        }
      }
    }
  }
`;

export const GlobalStyle = createGlobalStyle`
  /* Beautiful Select Dropdown Styling */
  .ant-select-dropdown {
    border-radius: 12px !important;
    box-shadow: 
      0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 10px 10px -5px rgba(0, 0, 0, 0.04),
      0 0 0 1px rgba(0, 0, 0, 0.05) !important;
    overflow: hidden;
    
    .ant-select-item {
      padding: 10px 16px;
      transition: all 0.2s ease;
      
      &:hover {
        background: #f8fafc;
      }
      
      &-option-selected {
        background: #eff6ff !important;
        font-weight: 600;
      }
    }
  }
  
  /* Date Picker Dropdown Styling */
  .ant-picker-dropdown {
    border-radius: 12px !important;
    box-shadow: 
      0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 10px 10px -5px rgba(0, 0, 0, 0.04),
      0 0 0 1px rgba(0, 0, 0, 0.05) !important;
      
    .ant-picker-panel {
      border-radius: 12px;
    }
    
    .ant-picker-cell-selected .ant-picker-cell-inner {
      background: #3b82f6 !important;
    }
    
    .ant-picker-today-btn {
      color: #3b82f6;
      
      &:hover {
        color: #2563eb;
      }
    }
  }
  
  /* Table Row Hover Effect */
  .ant-table-tbody > tr {
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateX(4px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      
      > td {
        background: rgba(255, 255, 255, 0.95) !important;
      }
    }
  }
  
  /* Disable hover effect for buttons */
  .no-hover-effect:hover {
    transform: none !important;
    box-shadow: none !important;
  }
  
  /* Beautiful scrollbar */
  ::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  
  ::-webkit-scrollbar-track {
    background: #f8fafc;
    border-radius: 10px;
  }
  
  ::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 10px;
    transition: background 0.3s ease;
    
    &:hover {
      background: #94a3b8;
    }
  }
  
  /* Tag styling */
  .ant-tag {
    font-weight: 500;
    transition: all 0.2s ease;
    
    &:hover {
      opacity: 0.8;
    }
  }
  
  /* Modal overlay backdrop blur */
  .ant-modal-wrap {
    backdrop-filter: blur(4px);
  }
  
  /* Button hover effects */
  .ant-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    &:hover {
      transform: translateY(-1px);
    }
    
    &:active {
      transform: translateY(0);
    }
  }
  
  /* Input focus effects */
  .ant-input,
  .ant-select-selector,
  .ant-picker {
    transition: all 0.3s ease !important;
    
    &:hover {
      border-color: #93c5fd !important;
    }
    
    &:focus,
    &.ant-input-focused,
    &.ant-select-focused .ant-select-selector,
    &.ant-picker-focused {
      border-color: #3b82f6 !important;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
    }
  }
  
  /* Beautiful tooltips */
  .ant-tooltip-inner {
    background: rgba(15, 23, 42, 0.95);
    backdrop-filter: blur(8px);
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 13px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
  
  .ant-tooltip-arrow-content {
    background: rgba(15, 23, 42, 0.95);
  }
  
  /* Animation for modals */
  .ant-modal {
    animation: modalFadeIn 0.3s ease-out;
  }
  
  @keyframes modalFadeIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;